import { useTranslation } from "@redotech/redo-i18n";
import { useWalkthrough } from "@redotech/redo-merchant-app-common/walkthrough/use-walkthrough";
import { RedoBadge } from "@redotech/redo-web/arbiter-components/badge/redo-badge";
import { RedoButton } from "@redotech/redo-web/arbiter-components/buttons/redo-button";
import { Flex } from "@redotech/redo-web/flex";
import { Text } from "@redotech/redo-web/text";
import * as darkModeCss from "@redotech/redo-web/theme/component-colors-dark.module.css";
import { memo } from "react";
import { NEW_HOME_WALKTHROUGH_ID } from "../../plg-homepage/walkthrough/target-ids";
import newHomePopupUrl from "../assets/new-home-popup.png?url";
import { OnboardingProps } from "../common";
import * as styles from "./new-home.module.css";

/**
 * The only way into the Home tour for merchants; staff use `?walkthrough`.
 * Shown only on the home page, so the tour starts in place; custom for its badge.
 */
export const NewHomeOnboarding = memo(function NewHomeOnboarding({
  handleDismiss,
  handleComplete,
}: OnboardingProps) {
  const { t } = useTranslation();
  const walkthrough = useWalkthrough();

  const handleTour = () => {
    handleComplete();
    walkthrough?.start(NEW_HOME_WALKTHROUGH_ID);
  };

  return (
    <Flex
      aria-labelledby="new-home-title"
      bgColor="tertiary"
      borderColor="primary"
      borderRadius="xl"
      borderStyle="solid"
      borderWidth="1px"
      className={darkModeCss.darkMode}
      data-testid="new-home-card"
      dir="column"
      gap="md"
      pb="xl"
      pt="lg"
      px="2xl"
      role="dialog"
      shadow="lg"
      w="xxs"
    >
      <Flex>
        <RedoBadge color="orange" size="sm" text={t("New")} />
      </Flex>
      <Flex dir="column" gap="xs">
        <Text
          fontSize="md"
          fontWeight="semibold"
          id="new-home-title"
          textColor="primary"
        >
          {t("Meet the new Home")}
        </Text>
        <Text fontSize="sm" textColor="secondary">
          {t(
            "Results, recent wins, and every {{brand}} product, together in one view.",
            { brand: "Redo" },
          )}
        </Text>
      </Flex>
      <Flex py="xs">
        <img
          alt={t("The new Home page")}
          className={styles.image}
          src={newHomePopupUrl}
        />
      </Flex>
      <Flex dir="column" gap="xxs">
        <Text fontSize="xs" fontWeight="semibold" textColor="tertiary">
          {t("Why the change")}
        </Text>
        <Text fontSize="sm" textColor="secondary">
          {t(
            "Running a brand takes enough attention. Seeing what {{brand}} delivers shouldn't take more, so the full picture now opens first.",
            { brand: "Redo" },
          )}
        </Text>
      </Flex>
      <Flex gap="xl">
        <RedoButton
          data-dd-action-name="new-home-take-tour"
          hierarchy="link"
          onClick={handleTour}
          text={t("Take a quick tour")}
          theme="action"
        />
        <RedoButton
          hierarchy="link"
          onClick={handleDismiss}
          text={t("Dismiss")}
        />
      </Flex>
    </Flex>
  );
});
